import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { PageArea, PageProps } from '../index'
import { pageComponent } from '../../higher-order-components/pageComponent'
import Header from './Header'
import { useTaskActualWorkApprovalStatusData } from './hooks/TaskActualWorkApproveStatusData'
import { TaskActualWorkApprovalStatusGridOptions } from './gridOptions'
import DateVO from '../../../vo/DateVO'
import { usePageState } from './hooks/pageState'
import Loading from '../../components/process-state-notifications/Loading'
import {
  ColDef,
  ColumnState,
  FilterChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { useExcel } from './hooks/excel'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import MultiSelectDialog from '../../components/dialogs/MultiSelectDialog'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { intl } from '../../../i18n'
import {
  useBulkSheetState,
  RestoreType,
} from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import _ from 'lodash'
import { useSearchCondition } from './hooks/useSearchCondition'

import { useDialog } from '../ProjectTaskActualWorkApprovalStatus/hooks/dialog'
import { fetchCount } from './taskActualWorkApprovalStatus'
import { setData } from '../../../store/singleSheet'
import { DecimalAndSexagesimalScale } from '../../../lib/functions/decimalAndSexagesimalScale'

type Props = PageProps

const TaskActualWorkApprovalStatus = (props: Props) => {
  const functionUuid = props.uuid
  const ref = useRef<HTMLDivElement>(null)
  const { data, loading, fetchRecords } = useTaskActualWorkApprovalStatusData()
  const pageState = usePageState({ functionUuid })
  const gridOptions = useMemo(() => {
    return TaskActualWorkApprovalStatusGridOptions(pageState.decimalScaleUnit)
  }, [pageState.decimalScaleUnit])
  const { initialized, displayDateTerm, handleMonthChange } = pageState

  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const [userCount, setUserCount] = useState<number>(0)
  const [isStateRestored, setIsStateRestored] = useState<boolean>(false)
  const restoreTypes = useMemo<RestoreType[]>(
    () => ['COLUMN_STATE', 'FILTER_STATE'],
    []
  )

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    'TaskActualWorkApprovalStatus',
    UiStateKey.BulkSheetUIStateColumnAndFilter,
    restoreTypes
  )

  const { dialogOpen, openDialog, closeDialog, onExportExcel } =
    useExcel(gridOptions)
  const columnSetting = useColumnSetting()
  const dialogState = useDialog()
  const { searchCondition, onChange: onSearchConditionChange } =
    useSearchCondition()

  const handleSearch = useCallback(() => {
    if (!initialized) return
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()
    const endDate = pageState.endDate.formatForApi()
    if (startDate && endDate) {
      fetchRecords(startDate, endDate, searchCondition)
    }
  }, [
    initialized,
    displayDateTerm,
    fetchRecords,
    searchCondition,
    pageState.endDate,
  ])

  const countUsers = useCallback(async () => {
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()!
    const endDate = pageState.endDate.formatForApi()!
    const response = await fetchCount(startDate, endDate, searchCondition)
    setUserCount(response.json)
  }, [displayDateTerm, searchCondition, pageState.endDate])

  useEffect(() => {
    countUsers()
  }, [countUsers])

  useEffect(() => {
    if (!initialized) return
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()
    const endDate = pageState.endDate.formatForApi()
    setData([])
    if (startDate && endDate) {
      fetchRecords(startDate, endDate, searchCondition)
    }
  }, [
    initialized,
    displayDateTerm,
    fetchRecords,
    pageState.endDate,
    searchCondition,
  ])

  useEffect(() => {
    if (!pageState.initialized || pageState.isCurrentMonth) return
    const lastDayOfMonth = new DateVO(displayDateTerm.endDate)
    if (pageState.endDate.formatForApi() !== lastDayOfMonth.formatForApi()) {
      pageState.setEndDate(lastDayOfMonth)
    }
  }, [
    pageState.initialized,
    pageState.isCurrentMonth,
    displayDateTerm,
    pageState,
  ])

  useEffect(() => {
    if (data.length > 0 && gridOptions.api) {
      setTimeout(() => {
        gridOptions.api?.refreshCells({
          columns: ['rowNumber'],
          force: true,
        })
      }, 100)
    }
  }, [data, gridOptions.api])

  useEffect(() => {
    gridOptions.api?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  useEffect(() => {
    if (
      bulkSheetState.initialized &&
      bulkSheetState.restored &&
      !isStateRestored
    ) {
      if (
        bulkSheetState.filterState &&
        Object.keys(bulkSheetState.filterState).length > 0
      ) {
        const filterKeys = Object.keys(bulkSheetState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      }

      if (bulkSheetState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] =
          bulkSheetState.columnState
            .filter(col => col.sort)
            .map(col => {
              const colDef = (gridOptions.columnDefs as ColDef[])?.find(
                def => def.colId === col.colId || def.field === col.colId
              )
              return {
                colId: col.colId,
                field: colDef?.field,
                headerName: colDef?.headerName,
                sort: col.sort!,
              }
            })
        setSortColumnsState(sortedColumnsState)
      }
    }
  }, [
    bulkSheetState.initialized,
    bulkSheetState.restored,
    bulkSheetState.filterState,
    bulkSheetState.columnState,
    gridOptions.columnDefs,
    isStateRestored,
  ])

  const refresh = useCallback(() => {
    if (!initialized) return
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()
    const endDate = pageState.endDate.formatForApi()
    if (startDate && endDate) {
      fetchRecords(startDate, endDate, searchCondition)
    }
  }, [
    initialized,
    displayDateTerm.startDate,
    pageState.endDate,
    fetchRecords,
    searchCondition,
  ])

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!column || !gridOptions.api) return
      const filterModel = gridOptions.api.getFilterModel()
      delete filterModel[column.colId || column.field || '']
      gridOptions.api.setFilterModel(filterModel)
    },
    [gridOptions.api]
  )

  const resetFilters = useCallback(() => {
    if (!gridOptions.api) return
    gridOptions.api.setFilterModel([])
  }, [gridOptions.api])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterState = e.api.getFilterModel()
      bulkSheetState.saveFilterState(filterState)
      setFilteredColumns(
        Object.keys(filterState)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v)
      )
    },
    [bulkSheetState]
  )

  const onSortedColumnsStateChanged = useCallback(
    (e: SortChangedEvent) => {
      const sortedColumns = e.columnApi
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => {
          return {
            ...e.api.getColumnDef(col.colId),
            colId: col.colId,
          }
        })
        .filter(v => !!v) as ColDef[]

      const columnState = gridOptions.columnApi?.getColumnState()
      const sortedColumnState: { [colId: string]: ColumnState } = {}
      columnState &&
        columnState.forEach(state => {
          if (state.sort) {
            sortedColumnState[state.colId] = state
          }
        })

      const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
        return {
          colId: col.colId,
          field: col.field,
          headerName: col.headerName,
          sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
        }
      })

      setSortColumnsState(sortedColumnsState)
    },
    [gridOptions.columnApi]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      const columnState = gridOptions.columnApi?.getColumnState()
      columnState && bulkSheetState.saveColumnState(columnState)
      onSortedColumnsStateChanged(e)
    },
    [bulkSheetState, gridOptions.columnApi, onSortedColumnsStateChanged]
  )

  const onGridReady = useCallback(
    params => {
      gridOptions.api = params.api
      gridOptions.columnApi = params.columnApi

      if (!_.isEmpty(bulkSheetState.columnState)) {
        gridOptions.columnApi?.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
      setIsStateRestored(true)
    },
    [bulkSheetState.columnState, gridOptions]
  )

  const onFirstDataRendered = useCallback(() => {
    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridOptions.api?.setFilterModel(bulkSheetState.filterState)
      if (bulkSheetState.filterState) {
        const filterKeys = Object.keys(bulkSheetState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      }
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState, gridOptions.api, gridOptions.columnDefs])

  const rememberColumnState = useCallback(() => {
    const columnState = gridOptions.columnApi?.getColumnState()
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState, gridOptions.columnApi])

  const onDeleteSortedColumn = useCallback((colId: string | ColDef<any>) => {
    if (!gridOptions.columnApi) return

    gridOptions.columnApi.applyColumnState({
      state: [{ colId: colId.toString(), sort: null }],
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridOptions.columnApi) return

    gridOptions.columnApi.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridOptions.columnApi])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridOptions.columnApi) return

      gridOptions.columnApi.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridOptions.columnApi]
  )

  const getCurrentUIState = useCallback(() => {
    return {
      columnState: bulkSheetState.columnState,
      filterState: bulkSheetState.filterState,
    }
  }, [bulkSheetState.columnState, bulkSheetState.filterState])

  const applyUIState = useCallback(
    (uiState: any) => {
      if (uiState.columnState && gridOptions.columnApi) {
        gridOptions.columnApi.applyColumnState({
          state: uiState.columnState,
          applyOrder: true,
        })
        bulkSheetState.saveColumnState(uiState.columnState)
      }
      if (uiState.filterState && gridOptions.api) {
        gridOptions.api.setFilterModel(uiState.filterState)
        bulkSheetState.saveFilterState(uiState.filterState)
      }
    },
    [bulkSheetState, gridOptions.api, gridOptions.columnApi]
  )

  if (!initialized || !bulkSheetState.initialized || !pageState.initialized) {
    return (
      <PageArea>
        <Loading isLoading={true} />
      </PageArea>
    )
  }

  return (
    <PageArea>
      <Header
        loading={loading}
        refresh={refresh}
        onMonthChange={handleMonthChange}
        currentYear={
          displayDateTerm
            ? new DateVO(displayDateTerm.startDate).getYear()
            : undefined
        }
        currentMonth={
          displayDateTerm
            ? new DateVO(displayDateTerm.startDate).getMonth()
            : undefined
        }
        searchCondition={searchCondition}
        onSearchConditionChange={onSearchConditionChange}
        onSearch={handleSearch}
        recordCount={data?.length}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        onClickExport={openDialog}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={pageState.rowHeight}
        onChangeRowHeight={(value: number) => {
          pageState.setRowHeight(value)
        }}
        userCount={userCount}
        decimalScaleUnit={pageState.decimalScaleUnit}
        onChangeDecimalScaleUnit={(unit: DecimalAndSexagesimalScale) => {
          pageState.setDecimalScaleUnit(unit)
        }}
        endDate={pageState.endDate}
        onEndDateChange={(date: DateVO) => {
          pageState.setEndDate(date)
        }}
        isCurrentMonth={pageState.isCurrentMonth}
        endDatePreset={pageState.endDatePreset}
        setEndDatePreset={pageState.setEndDatePreset}
      />
      <BulkSheetView
        ref={ref}
        rowData={data}
        {...gridOptions}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        onColumnMoved={rememberColumnState}
        onColumnResized={rememberColumnState}
        onColumnVisible={rememberColumnState}
        rowHeight={pageState.rowHeight}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        columnApi={gridOptions?.columnApi ?? undefined}
        gridApi={gridOptions?.api ?? undefined}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={dialogState.openUiState}
        initializeColumnState={() => {
          if (!gridOptions.columnApi || !gridOptions.api) return
          gridOptions.columnApi.resetColumnState()
          gridOptions.api.setFilterModel(null)
          bulkSheetState.saveColumnState([])
          bulkSheetState.saveFilterState({})
          setFilteredColumns([])
          setSortColumnsState([])
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={90}
      />
      <SavedUIStateDialog
        open={dialogState.uiState}
        onClose={dialogState.closeUiState}
        onSelect={selectedUIState => {
          const uiStateToApply = selectedUIState.UIState || selectedUIState
          applyUIState(uiStateToApply)
          dialogState.closeUiState()
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        title={intl.formatMessage({
          id: 'bulksheet.toolbar.columnQuickFilter.label.restore',
        })}
        sharable={false}
        currentUIState={getCurrentUIState()}
      />
      {dialogOpen && (
        <MultiSelectDialog
          onClose={closeDialog}
          onSubmit={(allList, selectedItem) => {
            const selectedColIds = selectedItem.map(col => col.value)
            onExportExcel(selectedColIds)
          }}
          dialogTitle={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.title',
          })}
          submitButtonTitle={intl.formatMessage({
            id: 'dialog.exceloutput.submit',
          })}
          allCheckBoxLabel={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.allcheckboxlabel',
          })}
          getSelectList={() => {
            return (gridOptions.columnApi?.getColumns() || [])
              .filter(
                column =>
                  !['uuid', 'drag', 'action', 'select', 'rowNumber'].includes(
                    column.getColId()
                  )
              )
              .map(column => {
                return {
                  value: column.getColId(),
                  displayName:
                    gridOptions.columnApi?.getDisplayNameForColumn(
                      column,
                      null
                    ) ?? '',
                  defaultChecked:
                    column.isVisible() ||
                    column.getColDef().cellRendererParams?.uiMeta?.tree,
                }
              })
          }}
          hideIcon={true}
        />
      )}
      <Loading isLoading={loading} elem={ref.current} />
    </PageArea>
  )
}

export default pageComponent(TaskActualWorkApprovalStatus)
